/**
 * Shared builders for Sitelist views
 * Used by both UI and export to ensure consistency
 */

export interface ResolvedRow {
  storeId: number;
  storeNumber?: string | null;
  storeName: string;
  storeAddress?: string | null;
  packId?: number | null;
  packName: string;
  groupId?: string | null;
  groupName?: string | null;
  formatId: number | null;
  formatName: string;
  quantity: number;
}

export interface SitelistByPackGroupRow {
  storeId: number;
  storeNumber?: string | null;
  storeName: string;
  storeAddress?: string | null;
  packGroupMembership: Record<string, 'YES' | 'NO'>;
}

export interface SitelistByFormatSection {
  packGroupName: string;
  stores: Array<{
    storeId: number;
    storeNumber?: string | null;
    storeName: string;
    storeAddress?: string | null;
    formatCounts: Record<string, number>;
  }>;
}

/**
 * Build "By Pack / Group" membership matrix
 * @param resolvedRows - Flattened resolved rows from campaign snapshot
 * @param campaignType - 'pack' or 'manual' (determines naming)
 */
export function buildSitelistByPackGroup(
  resolvedRows: ResolvedRow[],
  campaignType: 'pack' | 'manual'
): SitelistByPackGroupRow[] {
  // Get all unique stores
  const storeMap = new Map<number, {
    storeId: number;
    storeNumber?: string | null;
    storeName: string;
    storeAddress?: string | null;
  }>();

  // Get all unique pack/group names
  const packGroupNames = new Set<string>();

  // Process rows to build store map and collect pack/group names
  for (const row of resolvedRows) {
    if (!storeMap.has(row.storeId)) {
      storeMap.set(row.storeId, {
        storeId: row.storeId,
        storeNumber: row.storeNumber,
        storeName: row.storeName,
        storeAddress: row.storeAddress,
      });
    }

    // Use packName for PACK campaigns, groupName for MANUAL campaigns
    const packGroupName = campaignType === 'pack' ? row.packName : (row.groupName || row.packName);
    if (packGroupName) {
      packGroupNames.add(packGroupName);
    }
  }

  // Sort pack/group names alphabetically
  const sortedPackGroupNames = Array.from(packGroupNames).sort();

  // Build membership matrix
  const membershipMap = new Map<number, Map<string, boolean>>();

  for (const row of resolvedRows) {
    const packGroupName = campaignType === 'pack' ? row.packName : (row.groupName || row.packName);
    if (!packGroupName || row.quantity <= 0) continue;

    if (!membershipMap.has(row.storeId)) {
      membershipMap.set(row.storeId, new Map());
    }
    const storeMembership = membershipMap.get(row.storeId)!;
    storeMembership.set(packGroupName, true);
  }

  // Build result rows
  const result: SitelistByPackGroupRow[] = [];

  for (const [storeId, storeInfo] of storeMap.entries()) {
    const membership = membershipMap.get(storeId) || new Map();
    const packGroupMembership: Record<string, 'YES' | 'NO'> = {};

    for (const packGroupName of sortedPackGroupNames) {
      packGroupMembership[packGroupName] = membership.has(packGroupName) ? 'YES' : 'NO';
    }

    result.push({
      ...storeInfo,
      packGroupMembership,
    });
  }

  // Sort by store name
  result.sort((a, b) => a.storeName.localeCompare(b.storeName));

  return result;
}

/**
 * Build "By Format" quantity matrix grouped by pack/group
 * @param resolvedRows - Flattened resolved rows from campaign snapshot
 * @param campaignType - 'pack' or 'manual' (determines naming)
 */
export function buildSitelistByFormatCounts(
  resolvedRows: ResolvedRow[],
  campaignType: 'pack' | 'manual'
): SitelistByFormatSection[] {
  // Get all unique format names (global across all packs/groups)
  const formatNames = new Set<string>();
  for (const row of resolvedRows) {
    if (row.formatName) {
      formatNames.add(row.formatName);
    }
  }
  const sortedFormatNames = Array.from(formatNames).sort();

  // Group rows by pack/group
  const packGroupMap = new Map<string, ResolvedRow[]>();

  for (const row of resolvedRows) {
    const packGroupName = campaignType === 'pack' ? row.packName : (row.groupName || row.packName);
    if (!packGroupName) continue;

    if (!packGroupMap.has(packGroupName)) {
      packGroupMap.set(packGroupName, []);
    }
    packGroupMap.get(packGroupName)!.push(row);
  }

  // Build sections
  const sections: SitelistByFormatSection[] = [];

  // Sort pack/group names alphabetically
  const sortedPackGroupNames = Array.from(packGroupMap.keys()).sort();

  for (const packGroupName of sortedPackGroupNames) {
    const rows = packGroupMap.get(packGroupName)!;

    // Get unique stores in this pack/group
    const storeMap = new Map<number, {
      storeId: number;
      storeNumber?: string | null;
      storeName: string;
      storeAddress?: string | null;
      formatCounts: Map<string, number>;
    }>();

    for (const row of rows) {
      if (!storeMap.has(row.storeId)) {
        storeMap.set(row.storeId, {
          storeId: row.storeId,
          storeNumber: row.storeNumber,
          storeName: row.storeName,
          storeAddress: row.storeAddress,
          formatCounts: new Map(),
        });
      }

      const store = storeMap.get(row.storeId)!;
      const currentCount = store.formatCounts.get(row.formatName) || 0;
      store.formatCounts.set(row.formatName, currentCount + row.quantity);
    }

    // Convert to result format
    const stores = Array.from(storeMap.values()).map(store => {
      const formatCounts: Record<string, number> = {};
      for (const formatName of sortedFormatNames) {
        const count = store.formatCounts.get(formatName);
        if (count !== undefined && count > 0) {
          formatCounts[formatName] = count;
        }
      }

      return {
        storeId: store.storeId,
        storeNumber: store.storeNumber,
        storeName: store.storeName,
        storeAddress: store.storeAddress,
        formatCounts,
      };
    });

    // Sort stores by name
    stores.sort((a, b) => a.storeName.localeCompare(b.storeName));

    sections.push({
      packGroupName,
      stores,
    });
  }

  return sections;
}

/**
 * Build "By Label" quantity matrix grouped by retailer label
 * Groups rows by labelId from groups, with unlabelled groups under "Unlabelled"
 * @param resolvedRows - Flattened resolved rows from campaign snapshot
 * @param groups - UnifiedGroup[] to get labelId mapping
 * @param labelMap - Map of labelId -> labelName for display
 * @param campaignType - 'pack' or 'manual' (determines naming)
 */
export function buildSitelistByLabel(
  resolvedRows: ResolvedRow[],
  groups: Array<{ groupName: string; labelId?: number | null }>,
  labelMap: Map<number, string>,
  campaignType: 'pack' | 'manual'
): SitelistByFormatSection[] {
  // Build groupName -> labelId mapping
  const groupNameToLabelId = new Map<string, number | null>();
  for (const group of groups) {
    groupNameToLabelId.set(group.groupName, group.labelId ?? null);
  }

  // Get all unique format names (global across all labels)
  const formatNames = new Set<string>();
  for (const row of resolvedRows) {
    if (row.formatName) {
      formatNames.add(row.formatName);
    }
  }
  const sortedFormatNames = Array.from(formatNames).sort();

  // Group rows by labelId (via groupName lookup)
  const labelMap_rows = new Map<string, ResolvedRow[]>();
  const UNLABELLED_KEY = '__unlabelled__';

  for (const row of resolvedRows) {
    const packGroupName = campaignType === 'pack' ? row.packName : (row.groupName || row.packName);
    if (!packGroupName) continue;

    const labelId = groupNameToLabelId.get(packGroupName) ?? null;
    const labelKey = labelId !== null ? `label:${labelId}` : UNLABELLED_KEY;

    if (!labelMap_rows.has(labelKey)) {
      labelMap_rows.set(labelKey, []);
    }
    labelMap_rows.get(labelKey)!.push(row);
  }

  // Build sections
  const sections: SitelistByFormatSection[] = [];

  // Get all label IDs (including null for unlabelled)
  const labelKeys = Array.from(labelMap_rows.keys()).sort((a, b) => {
    // Put unlabelled last
    if (a === UNLABELLED_KEY) return 1;
    if (b === UNLABELLED_KEY) return -1;
    // Sort by label name
    const labelIdA = a ? parseInt(a.replace('label:', ''), 10) : null;
    const labelIdB = b ? parseInt(b.replace('label:', ''), 10) : null;
    const nameA = labelIdA && labelMap.has(labelIdA) ? labelMap.get(labelIdA)! : '';
    const nameB = labelIdB && labelMap.has(labelIdB) ? labelMap.get(labelIdB)! : '';
    return nameA.localeCompare(nameB);
  });

  for (const labelKey of labelKeys) {
    const rows = labelMap_rows.get(labelKey)!;
    const labelId = labelKey !== UNLABELLED_KEY ? parseInt(labelKey.replace('label:', ''), 10) : null;
    const labelName = labelId && labelMap.has(labelId) ? labelMap.get(labelId)! : 'Unlabelled';

    // Get unique stores in this label group
    const storeMap = new Map<number, {
      storeId: number;
      storeNumber?: string | null;
      storeName: string;
      storeAddress?: string | null;
      formatCounts: Map<string, number>;
    }>();

    for (const row of rows) {
      if (!storeMap.has(row.storeId)) {
        storeMap.set(row.storeId, {
          storeId: row.storeId,
          storeNumber: row.storeNumber,
          storeName: row.storeName,
          storeAddress: row.storeAddress,
          formatCounts: new Map(),
        });
      }

      const store = storeMap.get(row.storeId)!;
      const currentCount = store.formatCounts.get(row.formatName) || 0;
      store.formatCounts.set(row.formatName, currentCount + row.quantity);
    }

    // Convert to result format
    const stores = Array.from(storeMap.values()).map(store => {
      const formatCounts: Record<string, number> = {};
      for (const formatName of sortedFormatNames) {
        const count = store.formatCounts.get(formatName);
        if (count !== undefined && count > 0) {
          formatCounts[formatName] = count;
        }
      }

      return {
        storeId: store.storeId,
        storeNumber: store.storeNumber,
        storeName: store.storeName,
        storeAddress: store.storeAddress,
        formatCounts,
      };
    });

    // Sort stores by name
    stores.sort((a, b) => a.storeName.localeCompare(b.storeName));

    sections.push({
      packGroupName: labelName,
      stores,
    });
  }

  return sections;
}

/**
 * Build "By Label" membership matrix (YES/NO) - similar to pack-group view
 * Groups stores by retailer labels attached to groups, with unlabelled groups under "Unlabelled"
 * @param resolvedRows - Flattened resolved rows from campaign snapshot
 * @param groups - UnifiedGroup[] to get labelId mapping
 * @param labelMap - Map of labelId -> labelName for display
 * @param campaignType - 'pack' or 'manual' (determines naming)
 */
export function buildSitelistByLabelMembership(
  resolvedRows: ResolvedRow[],
  groups: Array<{ groupName: string; labelId?: number | null }>,
  labelMap: Map<number, string>,
  campaignType: 'pack' | 'manual'
): SitelistByPackGroupRow[] {
  // Build groupName -> labelId mapping
  const groupNameToLabelId = new Map<string, number | null>();
  for (const group of groups) {
    groupNameToLabelId.set(group.groupName, group.labelId ?? null);
  }

  // Get all unique stores
  const storeMap = new Map<number, {
    storeId: number;
    storeNumber?: string | null;
    storeName: string;
    storeAddress?: string | null;
  }>();

  // Get all unique label names (including "Unlabelled")
  const labelNames = new Set<string>();

  // Process rows to build store map and collect label names
  for (const row of resolvedRows) {
    if (!storeMap.has(row.storeId)) {
      storeMap.set(row.storeId, {
        storeId: row.storeId,
        storeNumber: row.storeNumber,
        storeName: row.storeName,
        storeAddress: row.storeAddress,
      });
    }

    const packGroupName = campaignType === 'pack' ? row.packName : (row.groupName || row.packName);
    if (!packGroupName) continue;

    const labelId = groupNameToLabelId.get(packGroupName) ?? null;
    const labelName = labelId !== null && labelMap.has(labelId) ? labelMap.get(labelId)! : 'Unlabelled';
    labelNames.add(labelName);
  }

  // Sort label names alphabetically, with "Unlabelled" last
  const sortedLabelNames = Array.from(labelNames).sort((a, b) => {
    if (a === 'Unlabelled') return 1;
    if (b === 'Unlabelled') return -1;
    return a.localeCompare(b);
  });

  // Build membership matrix: storeId -> Set of label names this store belongs to
  const membershipMap = new Map<number, Set<string>>();

  for (const row of resolvedRows) {
    const packGroupName = campaignType === 'pack' ? row.packName : (row.groupName || row.packName);
    if (!packGroupName || row.quantity <= 0) continue;

    const labelId = groupNameToLabelId.get(packGroupName) ?? null;
    const labelName = labelId !== null && labelMap.has(labelId) ? labelMap.get(labelId)! : 'Unlabelled';

    if (!membershipMap.has(row.storeId)) {
      membershipMap.set(row.storeId, new Set());
    }
    membershipMap.get(row.storeId)!.add(labelName);
  }

  // Build result rows
  const result: SitelistByPackGroupRow[] = [];

  for (const [storeId, storeInfo] of storeMap.entries()) {
    const membership = membershipMap.get(storeId) || new Set();
    const packGroupMembership: Record<string, 'YES' | 'NO'> = {};

    for (const labelName of sortedLabelNames) {
      packGroupMembership[labelName] = membership.has(labelName) ? 'YES' : 'NO';
    }

    result.push({
      ...storeInfo,
      packGroupMembership,
    });
  }

  // Sort by store name
  result.sort((a, b) => a.storeName.localeCompare(b.storeName));

  return result;
}
